import { Component, ComputedOptions, MethodOptions } from 'vue';
import { getUtilsCustomConfig } from '../config/custom';
import respCodeZhCn from '../resp/zh-cn';
export const DEFAULT_LANG = 'zh-cn';
interface RespMessageParam {
  /**
   * 相应错误码
   */
  code?: string;
  lang?: string;
  msgType?: 'success' | 'error' | 'warning' | 'info';
  offsetVal?: number;
  onClose?: () => void;
  dynDataList?: string[];
}

/**
 * 弹出后台返回的错误信息 -- 异步函数
 * @param code
 * @param lang 语言code 默认 zh-cn
 * @param msgType 消息类型 success/warning/info/error，默认 error
 * @param offsetVal Message 距离窗口顶部的偏移量 默认 10
 * @param onClose Message 关闭回调
 */
export function showRespMessage({
  code,
  lang = DEFAULT_LANG,
  msgType = 'error',
  offsetVal = 15,
  onClose,
  dynDataList = [],
}: RespMessageParam) {
  // 根据响应code获取提示信息
  const msgContent = getRespCodeMsg({ code, lang, dynDataList });

  if (!msgContent) {
    return;
  }

  // 弹出显示信息
  return showMessage({
    msgType,
    msgContent,
    offsetVal,
    onClose,
    dynDataList,
  });
}

type RespCodeMsgParam = {
  code?: string;
  lang?: string;
  dynDataList?: string[];
  forceShowMsg?: boolean;
};

/**
 * 获取响应码对应的中文信息
 * 如找不到对应的有效消息，返回当前code
 * @param code 相应错误码
 * @param lang 语言code 默认 zh-cn
 * @param dynDataList 对动态信息的支持处理，传对应错误信息替换
 */
export function getRespCodeMsg({ code, lang = DEFAULT_LANG, dynDataList, forceShowMsg = false }: RespCodeMsgParam) {
  if (code === undefined || code === null) {
    return '';
  }

  let result = `${code}`; // 默认值为code值

  // 获取响应码翻译后的内容
  const codeData = getLocalRespCodeData({ lang });
  if (!codeData) {
    return result; // 查找不到对应响应信息，直接返回code值
  }

  // 根据响应code获取提示信息
  const respCodeMsgObj = codeData[code];
  if (!respCodeMsgObj) {
    return result;
  }

  // 返回 msg 或者 desc 内容
  const { msg, desc, isDynMsg, enable = true } = respCodeMsgObj;

  if (!forceShowMsg && !enable) {
    // 不强制显示错误提示，且该提示不启用，则返回空（不显示提示）
    return '';
  }

  if (msg || desc) {
    result = msg || desc;
  }

  // 动态信息,需要通过外部传入参数来替换内容，
  // 例子:
  //  传入内容: '我是报错信息:$1,最大$2' 、['位置错误','100M！']
  //  结果：    '我是报错信息:位置错误,最大100M！'
  if (isDynMsg) {
    if (dynDataList?.length) {
      dynDataList.forEach((item, index) => {
        result = result.replace(`$${index}`, item);
      });
    } else {
      // 清除所有 $N, 替换为空格
      result = result.replace(/\$\d+/g, ' ');
    }
  }

  return result;
}

type Param4GetLocalRespCodeData = {
  lang?: string;
};
/**
 * 根据语言获取对应响应码信息
 * @param lang 语言code, zh-cn...
 */
export function getLocalRespCodeData({ lang = DEFAULT_LANG }: Param4GetLocalRespCodeData) {
  if (lang === DEFAULT_LANG) {
    return respCodeZhCn;
  }
  // TODO: 待以后扩展其他语言
}

type MessageParam = {
  msgContent?: string;
  msgType?: 'success' | 'error' | 'warning' | 'info';
  offsetVal?: number;
  duration?: number;
  neverHide?: boolean;
  classCode?: string;
  icon?: string | Component<any, any, any, ComputedOptions, MethodOptions>;
  onClose?: () => void;
  dynDataList?: string[];
};

/**
 * 显示信息弹窗 - 异步函数
 * @param msgType 消息类型 success/error，默认 error （warning和info待UI支持）
 * @param msgContent vNode or 消息内容
 * @param offsetVal Message 距离窗口顶部的偏移量 默认 130
 * @param duration 显示时间, 毫秒
 * @param neverHide 是否一直显示
 * @param icon 图标组件
 * @param onClose 关闭后回调
 * @param dynDataList
 */
export function showMessage({
  msgContent,
  msgType = 'error',
  offsetVal = 70,
  duration = 2000,
  neverHide = false,
  icon = '',
  classCode = 'lowcode-msg',
  onClose,
}: MessageParam) {
  // 显示信息框, 默认2s后自动关闭
  return new Promise((resolve) => {
    if (!msgContent) {
      console.error('msgContent不能为空');
      return;
    }

    // // 自定义图标的类名，会覆盖 type！！！
    // let iconClass = '';
    // switch (msgType) {
    //   case 'error':
    //     iconClass += 'msg-icon error lowcode-iconfont lowcode-icon-shibai';
    //     break;
    //   case 'warning':
    //     iconClass += 'msg-icon warning lowcode-iconfont lowcode-icon-shibai';
    //     break;
    //   case 'success':
    //     iconClass += 'msg-icon success lowcode-iconfont lowcode-icon-chenggong';
    //     break;
    //   default:
    //     break;
    // }

    // 弹窗实例
    let msgInst = null;
    // 250ms 是 Message 组件显示动画执行完成所需时间
    window.setTimeout(() => {
      const { elMessage } = getUtilsCustomConfig();
      msgInst = elMessage?.({
        type: msgType,
        message: msgContent,
        customClass: classCode, // 默认样式
        offset: offsetVal, // 离顶部距离
        // iconClass,
        icon,
        duration: neverHide ? 0 : duration,
        onClose,
      });

      resolve(msgInst);
    }, 250);
  });
}
